<template>
  <Listbox
    as="div"
    :class="{
      relative: selectorStyle !== 'filter',
      'min-w-0 grow': selectorStyle === 'label',
      'z-20': selectorStyle === 'filter',
      'opacity-60 select-none': disabled,
    }"
    v-model="spaces"
    :disabled="disabled"
  >
    <!-- Button -->
    <ListboxButton
      v-if="selectorStyle === 'dropdown'"
      class="relative w-full cursor-pointer rounded-sm border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500 sm:text-sm"
      :class="{
        '!cursor-not-allowed': disabled,
      }"
      @click.stop="openThrottled ? closeDropdown() : openDropdown()"
    >
      <span class="block truncate">
        {{ buttonLabel }}
      </span>
      <span
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2"
      >
        <ChevronDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
      </span>
      <div
        class="absolute inset-y-0 right-7 mx-2 flex items-center"
        v-if="hint"
      >
        <Tooltip :content="hint" origin="right">
          <HelpCircle class="w-4 h-4 text-gray-400" />
        </Tooltip>
      </div>
    </ListboxButton>
    <ListboxButton
      v-else
      @click.stop="openThrottled ? closeDropdown() : openDropdown()"
      :class="{
        'bg-sky-100 text-sky-800':
          selectorStyle === 'filter' && amountOfItems > 0,
        'text-gray-900 rounded-sm border-transparent bg-gray-100 hover:bg-sky-100 transition py-2 px-3 sm:text-sm':
          selectorStyle === 'filter',
        'text-sm text-gray-500 font-medium ': selectorStyle === 'label',
        '!cursor-not-allowed': disabled,
        'hover:text-gray-900 group': !disabled && selectorStyle === 'label',
      }"
      class="flex flex-nowrap items-center transition max-w-full"
    >
      <span v-if="multiple" class="flex items-center">
        {{ label }}
        <span
          v-if="amountOfItems > 0"
          class="ml-1 inline-flex items-center rounded-full bg-sky-200 px-2 py-0.5 text-xs font-medium text-sky-800"
        >
          {{ amountOfItems }}
        </span>
      </span>
      <span v-else class="flex flex-nowrap items-center max-w-full">
        <MapPinIcon
          class="h-5 w-5 text-gray-500 group-hover:text-gray-900"
          v-if="selectorStyle === 'label'"
        />
        <span
          class="ml-2 flex-1 whitespace-nowrap overflow-hidden text-ellipsis"
        >
          {{
            hasValue
              ? buttonLabel
              : selectorStyle === "label"
              ? t("components.inputs.expansion.no_value")
              : label
          }}
        </span>
      </span>
      <span class="ml-2" v-if="selectorStyle === 'filter'">
        <XMarkIcon
          v-if="hasValue"
          @click.stop="clear"
          class="h-5 w-5 text-sky-800"
          aria-hidden="true"
        />
        <ChevronDownIcon
          v-else
          class="h-5 w-5 text-gray-900"
          aria-hidden="true"
        />
      </span>
    </ListboxButton>

    <!-- Dropdown -->
    <transition
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        ref="selectorDropdown"
        v-if="openThrottled"
        static
        :class="[
          selectorStyle === 'label' ? `left-[-${horizontalLabelWidth}]` : '',
          selectorStyle === 'dropdown' ? 'w-full' : '',
        ]"
        class="flex flex-col z-20 mt-3 max-h-56 overflow-hidden max-w-md rounded-sm bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm absolute left-0 right-0 sm:left-auto sm:right-auto"
      >
        <FilterSearchInput
          v-model="searchQuery"
          :placeholder="searchPlaceholder"
        />

        <div class="overflow-auto">
          <tree
            v-if="spaces?.length > 0"
            :spaces="spaces"
            :fullHierarchy="hierarchy"
            :value="activeSpaceIds"
            :disable-tenants="!allowTenantSelection"
            :single="!multiple"
            :tenant-id="tenantId"
            :isSearching="searchQuery.length > 0"
            @checked="handleChecked"
            :cascadeCheck="cascadeCheck"
          />
          <div v-else class="grow px-3 py-2 text-gray-500">
            {{ t("components.no_result") }}
          </div>
        </div>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import { Listbox, ListboxButton, ListboxOptions } from "@headlessui/vue";
import { XMarkIcon, ChevronDownIcon } from "@heroicons/vue/20/solid";
import { MapPinIcon } from "@heroicons/vue/24/outline";
import { useI18n } from "vue-i18n";
import { onClickOutside, refThrottled } from "@vueuse/core";

import FilterSearchInput from "@/Tailwind/Components/Input/Filters/FilterSearchInput";
import Tree from "@/Tailwind/Components/Tree/Tree";
import HelpCircle from "@/Tailwind/Icon/HelpCircle.vue";
import Tooltip from "@/Tailwind/Components/Tooltip.vue";

const { t } = useI18n();

const props = defineProps({
  label: String,
  searchPlaceholder: String,
  hierarchy: Array,
  value: [Object, Array, String],
  disabled: Boolean,
  tenantId: [Array, String],
  selectorStyle: {
    type: String,
    default: "filter",
    validator(value) {
      // The value must match one of these strings
      return ["filter", "label", "dropdown"].includes(value);
    },
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  allowTenantSelection: {
    type: Boolean,
    default: false,
  },
  horizontalLabelWidth: {
    type: String,
    default: "0rem",
  },
  hint: {
    type: String,
  },
  cascadeCheck: {
    type: Boolean,
  },
});

const emit = defineEmits(["change"]);

const hierarchy = ref(props.hierarchy);
watch(
  () => props.hierarchy,
  () => {
    hierarchy.value = props.hierarchy;
  }
);

const activeSpaceIds = computed(() => {
  if (Array.isArray(props.value)) {
    return props.value;
  }

  // If value is string, we assume it's the id of the space
  if (typeof props.value === "string") {
    return [props.value];
  }

  return props.value ? [props.value?.id] : [];
});

// Search
const searchQuery = ref("");
watch(searchQuery, () => {
  if (searchQuery.value?.length === 0) {
    spaces.value = hierarchy.value;
    return;
  }

  return (spaces.value = performSearch(hierarchy.value, searchQuery.value));
});

function performSearch(root, query) {
  const result = [];
  root.forEach((node) => {
    let children;

    if (
      node.name.toLowerCase().includes(query.toLowerCase()) ||
      node.identifier?.toLowerCase().includes(query.toLowerCase())
    ) {
      // Also filter node if it has children that match the query
      children = performSearch(node.children || [], query);

      result.push({ ...node, children });

      return;
    }

    children = performSearch(node.children || [], query);

    if (children.length) {
      result.push(Object.assign({}, node, { children }));
    }
  });

  return result;
}

// Open state
const selectorDropdown = ref(null);
const open = ref(false);
const openThrottled = refThrottled(open, 100, true, false);

const openDropdown = () => {
  if (openThrottled.value !== open.value) {
    return;
  }

  open.value = true;
};

const closeDropdown = () => {
  if (openThrottled.value !== open.value) {
    return;
  }

  open.value = false;
};

onClickOutside(selectorDropdown, () => {
  if (!open.value) {
    return;
  }

  closeDropdown();
});

// Space selection
const spaces = ref(props.hierarchy);
watch(
  () => props.hierarchy,
  () => {
    spaces.value = props.hierarchy;
  }
);

function handleChecked(value) {
  if (!props.multiple) {
    closeDropdown();
    emit("change", value);

    return;
  }

  emit("change", value);
}

const hasValue = computed(() => {
  if (!props.value) {
    return false;
  }

  if (!props.multiple) {
    return typeof props.value === "string" || !!props.value?.id;
  }

  return props.value?.length > 0;
});

const amountOfItems = computed(() => {
  if (!props.value) {
    return 0;
  }

  if (!props.multiple) {
    return typeof props.value === "string" || props.value?.id ? 1 : 0;
  }

  return props.value?.length;
});

const buttonLabel = computed(() => {
  if (!hasValue.value) {
    return t("components.spaces.none");
  }

  if (!props.multiple) {
    return typeof props.value === "string"
      ? findLocationRecursive(spaces.value, props.value).name
      : props.value?.name;
  }

  return props.value?.map((space) => space.name).join(", ");
});

const findLocationRecursive = (root, id) => {
  let result = null;
  root.forEach((node) => {
    if (node.id === id) {
      result = node;
      return;
    }

    if (node.children) {
      const childResult = findLocationRecursive(node.children, id);
      if (childResult) {
        result = childResult;
        return;
      }
    }
  });

  return result;
};

function clear() {
  handleChecked(props.multiple ? [] : {});
}
</script>

<style>
.tree-container ul {
  @apply overflow-visible !important;
}

.node > .row_data {
  @apply flex items-center space-x-2 pl-3 hover:bg-gray-100;
}

.node input[type="checkbox"] {
  @apply h-4 w-4 rounded-xs border-gray-300 text-brand-900 focus:outline-0 focus:ring-offset-0 focus:ring-0;
}
</style>
